/**
 * 选品配置视图 — A 线蓝海 / B 线卖家精灵双轨 + 遗留旧采集
 * 仅管理员可访问
 */
import { registerViewLoader } from './navigation.js';
import { showToast } from './utils.js';
import { apiGet, apiPost, apiPut } from './product-research-config-api.js';
import {
  appendConfigText,
  createConfigElement,
  createConfigEmptyStatus,
  createConfigStatus,
  formatConfigTime as formatTime,
  padConfigNumber as pad2,
  replaceConfigChildren,
  setConfigButtonLoading as setButtonLoading,
  setConfigFeedback as setFeedback,
  setConfigText as setText,
  setConfigValue as setValue,
} from './product-research-config-dom.js';
import {
  createConfigJobCard,
  getConfigCheckedValues as getCheckedValues,
  renderConfigCheckboxes as renderCheckboxes,
  setConfigCheckedValues as setCheckedValues,
  statusClassFor,
} from './product-research-config-view.js';
import { createProductResearchMirrorController } from './product-research-mirror-controller.js';

const MIRROR_SITE_CODES = ['US', 'UK', 'DE', 'FR', 'IT', 'CA'];

let initialized = false;
let eventsBound = false;
let jobPollingTimer = null;
let hasLoadedJobsOnce = false;
let lastKnownJobStatuses = new Map();
const announcedCompletedJobIds = new Set();
const watchedJobTimers = new Map();
let sitesCache = [];
let aRunSiteCodes = null;

const mirrorController = createProductResearchMirrorController({
  apiGet,
  apiPost,
  apiPut,
  appendText: appendConfigText,
  createElement: createConfigElement,
  createEmptyStatus: createConfigEmptyStatus,
  createJobCard: createConfigJobCard,
  createStatus: createConfigStatus,
  getCheckedValues,
  getElementById: (id) => document.getElementById(id),
  pad2,
  replaceChildren: replaceConfigChildren,
  setButtonLoading,
  setCheckedValues,
  setFeedback,
  setText,
  showToast,
  confirmFn: (...args) => confirm(...args),
});

export function initProductResearchConfig() {
  if (!initialized) {
    initialized = true;
    registerViewLoader('admin-product-research-config', () => {
      loadAll();
      startJobPolling();
    });
  }

  if (eventsBound || !document.getElementById('view-admin-product-research-config')) return;
  bindEvents();
  eventsBound = true;
}

function bindEvents() {
  const $ = (id) => document.getElementById(id);

  document.querySelectorAll('[data-pr-track]').forEach((tab) => {
    tab.addEventListener('click', () => switchTrack(tab.dataset.prTrack));
  });

  // B 线账号
  $('pr-config-save-credentials')?.addEventListener('click', saveCredentials);
  $('pr-config-refresh-login')?.addEventListener('click', refreshLogin);
  $('pr-config-refresh-status')?.addEventListener('click', loadAuthStatus);

  // A 线
  $('pr-a-schedule-save')?.addEventListener('click', saveASchedule);
  $('pr-a-run-now')?.addEventListener('click', runANow);
  $('pr-a-runs-refresh')?.addEventListener('click', loadASchedule);
  $('pr-a-run-site-checkboxes')?.addEventListener('change', () => {
    aRunSiteCodes = getCheckedValues('pr-a-run-site-checkboxes');
  });

  // B 线
  $('pr-b-schedule-save')?.addEventListener('click', () => mirrorController.saveSchedule());
  $('pr-b-run-now')?.addEventListener('click', () => mirrorController.runNow());
  $('pr-b-runs-refresh')?.addEventListener('click', () => mirrorController.loadSchedule());
  $('pr-b-run-site-checkboxes')?.addEventListener('change', () => mirrorController.rememberRunSiteSelection());
  $('pr-b-matrix-half')?.addEventListener('click', () => mirrorController.runMatrix('half'));
  $('pr-b-matrix-full')?.addEventListener('click', () => mirrorController.runMatrix('full'));
  $('pr-b-matrix-refresh')?.addEventListener('click', () => mirrorController.loadMatrix());

  // 遗留
  $('pr-config-sync-models')?.addEventListener('click', syncModels);
  $('pr-config-submit-collection')?.addEventListener('click', submitCollection);
  $('pr-config-toggle-all-collection')?.addEventListener('click', toggleCollectionSelections);
  $('pr-config-site-checkboxes')?.addEventListener('change', syncCollectionSelectionToggle);
  $('pr-config-model-checkboxes')?.addEventListener('change', syncCollectionSelectionToggle);
  $('pr-config-refresh-jobs')?.addEventListener('click', loadJobs);
  $('pr-config-jobs-list')?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-pr-cancel-job-id]');
    if (!button) return;
    void cancelJob(button.dataset.prCancelJobId);
  });
}

function switchTrack(track) {
  document.querySelectorAll('[data-pr-track]').forEach((tab) => {
    const active = tab.dataset.prTrack === track;
    tab.classList.toggle('is-active', active);
    tab.setAttribute('aria-selected', String(active));
  });
  document.querySelectorAll('[data-pr-track-panel]').forEach((panel) => {
    panel.hidden = panel.dataset.prTrackPanel !== track;
  });
}

async function loadAll() {
  await Promise.all([
    loadSitesAndModels(),
    loadAuthStatus(),
    loadASchedule(),
    mirrorController.loadSchedule(),
    mirrorController.loadMatrix(),
    loadJobs(),
  ]);
}

// ==================== 站点复选框 ====================

async function loadSitesAndModels() {
  try {
    const [sites, models] = await Promise.all([
      apiGet('/api/product-research/sites'),
      apiGet('/api/product-research/models').catch(() => []),
    ]);
    sitesCache = Array.isArray(sites) ? sites : [];
    const mirrorSites = sitesCache.filter((s) => MIRROR_SITE_CODES.includes(String(s.code || s.id || '').toUpperCase()));

    renderCheckboxes('pr-a-schedule-site-checkboxes', sitesCache, 'a-sched');
    renderCheckboxes('pr-a-run-site-checkboxes', sitesCache, 'a-run', aRunSiteCodes);
    renderCheckboxes('pr-b-schedule-site-checkboxes', mirrorSites.length ? mirrorSites : MIRROR_SITE_CODES.map((code) => ({ code, name: code })), 'b-sched');
    renderCheckboxes('pr-b-run-site-checkboxes', mirrorSites.length ? mirrorSites : MIRROR_SITE_CODES.map((code) => ({ code, name: code })), 'b-run');
    if (aRunSiteCodes?.length) setCheckedValues('pr-a-run-site-checkboxes', aRunSiteCodes);
    mirrorController.restoreRunSiteSelection();
    renderCheckboxes('pr-config-site-checkboxes', sitesCache, 'site');
    renderCheckboxes('pr-config-model-checkboxes', models, 'model');
    syncCollectionSelectionToggle();
    setText('pr-config-models-count', `当前共 ${models.length} 个模型`);
  } catch (err) {
    setText('pr-config-models-count', '加载失败: ' + err.message);
  }
}

// ==================== A 线 ====================

async function loadASchedule() {
  try {
    const data = await apiGet('/api/product-research/selection/schedule');
    const schedule = data.schedule || {};
    const enabled = document.getElementById('pr-a-schedule-enabled');
    if (enabled) enabled.checked = Boolean(schedule.enabled);
    setValue('pr-a-schedule-time', `${String(schedule.hour ?? 2).padStart(2, '0')}:${String(schedule.minute ?? 0).padStart(2, '0')}`);
    setValue('pr-a-schedule-interval', schedule.intervalDays ?? 2);
    setValue('pr-a-schedule-timezone', schedule.timezone || 'Asia/Shanghai');
    if (Array.isArray(schedule.siteCodes) && schedule.siteCodes.length) {
      setCheckedValues('pr-a-schedule-site-checkboxes', schedule.siteCodes);
    }
    if (!aRunSiteCodes) {
      aRunSiteCodes = Array.isArray(schedule.siteCodes) && schedule.siteCodes.length
        ? [...schedule.siteCodes]
        : ['US'];
    }
    setCheckedValues('pr-a-run-site-checkboxes', aRunSiteCodes);
    const status = document.getElementById('pr-a-schedule-status');
    if (status) {
      replaceConfigChildren(
        status,
        createConfigStatus(schedule.enabled ? 'pr-config-status-ok' : 'pr-config-status-muted', schedule.enabled ? '已启用' : '未启用'),
      );
      appendConfigText(status, ` · ${schedule.registered ? '任务已注册' : '任务未注册'} · 每 ${schedule.intervalDays ?? 2} 天 ${pad2(schedule.hour)}:${pad2(schedule.minute)}（${schedule.timezone || 'Asia/Shanghai'}） · 站点 ${(schedule.siteCodes || []).join(', ') || '-'}`);
    }
    renderARuns(data.latestRuns || []);
  } catch (err) {
    setText('pr-a-schedule-status', '加载失败: ' + err.message);
    setText('pr-a-runs-list', '加载失败: ' + err.message);
  }
}

function renderARuns(runs) {
  const el = document.getElementById('pr-a-runs-list');
  if (!el) return;
  if (!runs.length) {
    replaceConfigChildren(el, createConfigEmptyStatus('pr-config-status-muted', '暂无 A 线执行记录'));
    return;
  }
  replaceConfigChildren(el, ...runs.slice(0, 15).map((run) => createConfigJobCard(run)));
}

async function saveASchedule() {
  const button = document.getElementById('pr-a-schedule-save');
  if (button) button.disabled = true;
  try {
    const siteCodes = getCheckedValues('pr-a-schedule-site-checkboxes');
    if (!siteCodes.length) throw new Error('请至少选择一个可调度站点');
    const time = document.getElementById('pr-a-schedule-time')?.value || '02:00';
    await apiPut('/api/product-research/selection/schedule', {
      enabled: Boolean(document.getElementById('pr-a-schedule-enabled')?.checked),
      hour: Number(time.split(':')[0]),
      minute: Number(time.split(':')[1]),
      intervalDays: Number(document.getElementById('pr-a-schedule-interval')?.value || 2),
      timezone: document.getElementById('pr-a-schedule-timezone')?.value || 'Asia/Shanghai',
      strategy: 'low-competition-profit',
      site_codes: siteCodes,
    });
    showToast('A 线调度已保存并应用', 'success');
    await loadASchedule();
  } catch (err) {
    showToast(err.message || '保存失败', 'error');
  } finally {
    if (button) button.disabled = false;
  }
}

async function runANow() {
  const siteCodes = getCheckedValues('pr-a-run-site-checkboxes');
  if (!siteCodes.length) {
    setFeedback('pr-a-run-feedback', '请至少选择一个站点', 'error');
    return;
  }
  setButtonLoading('pr-a-run-now', true);
  setFeedback('pr-a-run-feedback', `正在提交 ${siteCodes.join('、')} …`, 'info');
  try {
    const data = await apiPost('/api/product-research/selection/run', {
      site_codes: siteCodes,
      strategy_code: 'low-competition-profit',
      include_signals: true,
    });
    setFeedback('pr-a-run-feedback', `已提交任务 ${data?.jobId || '-'}`, 'success');
    showToast('A 线任务已提交', 'success');
    await loadASchedule();
  } catch (err) {
    setFeedback('pr-a-run-feedback', '提交失败: ' + err.message, 'error');
  } finally {
    setButtonLoading('pr-a-run-now', false);
  }
}

// ==================== B 线 ====================

// ==================== 凭证 + 登录态 ====================

async function loadAuthStatus() {
  try {
    const data = await apiGet('/api/product-research/auth/status');
    renderAuthStatus(data);
    renderCredentialsStatus(data);
  } catch (err) {
    setText('pr-config-login-status', '加载失败: ' + err.message);
    setText('pr-config-credentials-status', '加载失败');
  }
}

function renderCredentialsStatus(data) {
  const el = document.getElementById('pr-config-credentials-status');
  if (!el) return;
  if (data.credentialsConfigured) {
    replaceConfigChildren(
      el,
      createConfigStatus('pr-config-status-ok', '✓ 已配置'),
      document.createTextNode(' — 用户名: '),
      createConfigElement('strong', '', data.credentialsUsername || ''),
    );
  } else {
    replaceConfigChildren(el, createConfigStatus('pr-config-status-error', '✗ 未配置'));
  }
  if (data.credentialsUsername) {
    const usernameInput = document.getElementById('pr-config-username');
    if (usernameInput && !usernameInput.value) usernameInput.value = data.credentialsUsername;
  }
}

function renderAuthStatus(data) {
  const el = document.getElementById('pr-config-login-status');
  if (!el) return;
  const loggedIn = data.configured === true || data.loggedIn || data.status === 'logged_in';
  if (loggedIn) {
    if (data.stale) {
      const ageHint = data.ageHours != null ? `（${data.ageHours} 小时前保存）` : '';
      replaceConfigChildren(
        el,
        createConfigStatus('pr-config-status-error', `⚠ 登录态可能已过期${ageHint}`),
        createConfigElement('br'),
        createConfigElement('span', 'pr-config-job-meta', '建议点击「自动重新登录」刷新'),
      );
    } else {
      replaceConfigChildren(el, createConfigStatus('pr-config-status-ok', '✓ 已登录'));
      if (data.savedAt || data.expiresAt) {
        el.append(
          createConfigElement('br'),
          createConfigElement('span', 'pr-config-job-meta', `登录时间: ${formatTime(data.savedAt || data.expiresAt)}`),
        );
      }
    }
  } else {
    replaceConfigChildren(
      el,
      createConfigStatus('pr-config-status-error', '✗ 未登录'),
      createConfigElement('br'),
      createConfigElement('span', 'pr-config-job-meta', '点击「自动重新登录」刷新登录态'),
    );
  }
  if (data.activeCollectionJob) {
    const job = data.activeCollectionJob;
    el.append(
      createConfigElement('br'),
      createConfigElement('span', 'pr-config-job-meta pr-config-status-info', `当前有采集任务进行中: ${job.status || ''}`),
    );
  }
}

async function saveCredentials() {
  const username = document.getElementById('pr-config-username')?.value.trim();
  const password = document.getElementById('pr-config-password')?.value;
  if (!username || !password) {
    setFeedback('pr-config-login-feedback', '请填写用户名和密码', 'error');
    return;
  }
  setButtonLoading('pr-config-save-credentials', true);
  try {
    await apiPut('/api/product-research/credentials', { username, password });
    setFeedback('pr-config-login-feedback', '账号保存成功', 'success');
    document.getElementById('pr-config-password').value = '';
    await loadAuthStatus();
  } catch (err) {
    setFeedback('pr-config-login-feedback', '保存失败: ' + err.message, 'error');
  } finally {
    setButtonLoading('pr-config-save-credentials', false);
  }
}

async function refreshLogin() {
  setButtonLoading('pr-config-refresh-login', true);
  setFeedback('pr-config-login-feedback', '正在重新登录，请稍候…', 'info');
  try {
    await apiPost('/api/product-research/auth/login');
    setFeedback('pr-config-login-feedback', '登录成功', 'success');
    await loadAuthStatus();
  } catch (err) {
    setFeedback('pr-config-login-feedback', '登录失败: ' + err.message, 'error');
  } finally {
    setButtonLoading('pr-config-refresh-login', false);
  }
}

// ==================== 遗留：模型 + 旧采集 ====================

async function syncModels() {
  setButtonLoading('pr-config-sync-models', true);
  setFeedback('pr-config-sync-feedback', '正在同步模型模板…', 'info');
  try {
    const data = await apiPost('/api/product-research/models/sync');
    setFeedback('pr-config-sync-feedback', `同步成功，共 ${data?.models?.length || data?.length || 0} 个模型`, 'success');
    await loadSitesAndModels();
  } catch (err) {
    setFeedback('pr-config-sync-feedback', '同步失败: ' + err.message, 'error');
  } finally {
    setButtonLoading('pr-config-sync-models', false);
  }
}

function getCollectionSelectionCheckboxes() {
  return [
    ...document.querySelectorAll('#pr-config-site-checkboxes input[type="checkbox"]'),
    ...document.querySelectorAll('#pr-config-model-checkboxes input[type="checkbox"]'),
  ];
}

function toggleCollectionSelections() {
  const checkboxes = getCollectionSelectionCheckboxes();
  const shouldSelectAll = checkboxes.some((checkbox) => !checkbox.checked);
  checkboxes.forEach((checkbox) => {
    checkbox.checked = shouldSelectAll;
  });
  syncCollectionSelectionToggle();
}

function syncCollectionSelectionToggle() {
  const button = document.getElementById('pr-config-toggle-all-collection');
  if (!button) return;
  const checkboxes = getCollectionSelectionCheckboxes();
  const allSelected = checkboxes.length > 0 && checkboxes.every((checkbox) => checkbox.checked);
  button.disabled = checkboxes.length === 0;
  button.setAttribute('aria-pressed', String(allSelected));
  button.textContent = allSelected ? '取消全选' : '全选站点和模型';
}

async function submitCollection() {
  const siteCodes = getCheckedValues('pr-config-site-checkboxes');
  const modelCodes = getCheckedValues('pr-config-model-checkboxes');
  const targetCount = Number(document.getElementById('pr-config-target-count')?.value) || 600;

  if (siteCodes.length === 0 || modelCodes.length === 0) {
    setFeedback('pr-config-submit-feedback', '请至少选择一个站点和一个模型', 'error');
    return;
  }

  setButtonLoading('pr-config-submit-collection', true);
  setFeedback('pr-config-submit-feedback', '正在提交采集任务…', 'info');
  try {
    const data = await apiPost('/api/product-research/collect/run', { site_codes: siteCodes, model_codes: modelCodes, target_count: targetCount });
    setFeedback('pr-config-submit-feedback', `采集任务已提交，队列位置: ${data.queue_position || 1}`, 'success');
    watchCollectionJob(data.job_id);
    await loadJobs();
  } catch (err) {
    setFeedback('pr-config-submit-feedback', '提交失败: ' + err.message, 'error');
  } finally {
    setButtonLoading('pr-config-submit-collection', false);
  }
}

async function loadJobs() {
  try {
    const data = await apiGet('/api/product-research/collect/jobs?limit=10');
    renderJobs(data);
  } catch (err) {
    setText('pr-config-jobs-list', '加载失败: ' + err.message);
  }
}

function renderJobs(data) {
  const jobs = Array.isArray(data) ? data : (Array.isArray(data?.jobs) ? data.jobs : []);
  const summaryEl = document.getElementById('pr-config-jobs-summary');
  const listEl = document.getElementById('pr-config-jobs-list');
  if (!summaryEl || !listEl) return;

  handleCompletedJobNotice(jobs);
  jobs.forEach((job) => {
    if (isActiveJobStatus(job.status)) watchCollectionJob(job.id);
  });

  if (jobs.length === 0) {
    replaceConfigChildren(summaryEl, createConfigEmptyStatus('pr-config-status-muted', '暂无采集任务'));
    replaceConfigChildren(listEl);
    return;
  }

  const running = jobs.filter((j) => j.status === 'running' || j.status === 'queued');
  const completed = jobs.filter((j) => j.status === 'completed' || j.status === 'success');
  const failed = jobs.filter((j) => j.status === 'failed' || j.status === 'cancelled');
  replaceConfigChildren(
    summaryEl,
    document.createTextNode(`共 ${jobs.length} 个任务 — `),
    createConfigStatus('pr-config-status-info', `进行中 ${running.length}`),
    document.createTextNode(' / '),
    createConfigStatus('pr-config-status-ok', `已完成 ${completed.length}`),
    document.createTextNode(' / '),
    createConfigStatus('pr-config-status-error', `失败/取消 ${failed.length}`),
  );

  replaceConfigChildren(listEl, ...jobs.map((job) => {
    const canCancel = job.status === 'running' || job.status === 'queued' || job.status === 'cancel_requested';
    const card = createConfigElement('div', 'pr-config-job-card');
    const head = createConfigElement('div', 'pr-config-job-head');
    const title = createConfigElement('div');
    title.append(
      createConfigElement('strong', '', job.id || ''),
      createConfigStatus(`${statusClassFor(job.status)} pr-config-status-label`, job.status || ''),
    );
    const actions = createConfigElement('div', 'pr-config-job-actions');
    if (canCancel) {
      const cancel = createConfigElement('button', 'btn btn-secondary', '取消');
      cancel.type = 'button';
      cancel.dataset.prCancelJobId = String(job.id || '');
      actions.append(cancel);
    }
    const sitesText = job.requested_site_codes?.length ? job.requested_site_codes.join(', ') : '-';
    const modelsText = job.requested_model_codes?.length ? job.requested_model_codes.join(', ') : '-';
    const meta = createConfigElement('div', 'pr-config-job-meta', `进度 ${job.finished_count || 0}/${job.total_count || 0} · 站点 ${sitesText} · 模型 ${modelsText} · ${formatTime(job.started_at)}`);
    head.append(title, actions);
    card.append(head, meta);
    if (job.error_message) card.append(createConfigElement('div', 'pr-config-job-error', job.error_message));
    return card;
  }));
}

async function cancelJob(jobId) {
  if (!jobId) return;
  if (!confirm('确认取消该采集任务？')) return;
  try {
    await apiPost('/api/product-research/collect/cancel', { job_id: jobId });
    setFeedback('pr-config-submit-feedback', '已请求取消采集任务', 'success');
    await loadJobs();
  } catch (err) {
    showToast('取消失败: ' + err.message, 'error');
  }
}

function handleCompletedJobNotice(jobs) {
  const nextStatuses = new Map(jobs.map((job) => [String(job.id || ''), String(job.status || '')]));
  if (hasLoadedJobsOnce) {
    const newlyCompletedJob = jobs.find((job) => {
      const jobId = String(job.id || '');
      const currentStatus = normalizeJobStatus(job.status);
      const previousStatus = normalizeJobStatus(lastKnownJobStatuses.get(jobId));
      if (!jobId || announcedCompletedJobIds.has(jobId)) return false;
      return currentStatus === 'completed' && previousStatus && previousStatus !== 'completed';
    });
    if (newlyCompletedJob) {
      announcedCompletedJobIds.add(String(newlyCompletedJob.id));
      showToast('旧采集已完成，可立即查看结果', 'success');
    }
  }
  lastKnownJobStatuses = nextStatuses;
  hasLoadedJobsOnce = true;
}

function normalizeJobStatus(status) {
  if (status === 'completed' || status === 'success') return 'completed';
  if (status === 'queued' || status === 'running' || status === 'cancel_requested') return 'active';
  if (status === 'failed' || status === 'cancelled') return 'terminal';
  return status || '';
}

function watchCollectionJob(jobId) {
  const normalizedJobId = String(jobId || '').trim();
  if (!normalizedJobId || watchedJobTimers.has(normalizedJobId) || announcedCompletedJobIds.has(normalizedJobId)) {
    return;
  }
  const poll = async () => {
    try {
      const status = await apiGet(`/api/product-research/collect/status?job_id=${encodeURIComponent(normalizedJobId)}`);
      if (!status) return;
      if (isCompletedJobStatus(status.status)) {
        stopWatchingCollectionJob(normalizedJobId);
        announcedCompletedJobIds.add(normalizedJobId);
        showToast('采集已完成，可立即查看结果', 'success');
        await loadJobs();
        return;
      }
      if (isTerminalJobStatus(status.status)) {
        stopWatchingCollectionJob(normalizedJobId);
        await loadJobs();
      }
    } catch (error) {
      console.warn('[product-research-config] job status poll failed', normalizedJobId, error?.message || error);
    }
  };
  const timer = window.setInterval(() => { void poll(); }, 3000);
  watchedJobTimers.set(normalizedJobId, timer);
  void poll();
}

function stopWatchingCollectionJob(jobId) {
  const normalizedJobId = String(jobId || '').trim();
  const timer = watchedJobTimers.get(normalizedJobId);
  if (timer) {
    clearInterval(timer);
    watchedJobTimers.delete(normalizedJobId);
  }
}

function isActiveJobStatus(status) {
  return status === 'queued' || status === 'running' || status === 'cancel_requested';
}

function isCompletedJobStatus(status) {
  return status === 'completed' || status === 'success';
}

function isTerminalJobStatus(status) {
  return isCompletedJobStatus(status) || status === 'failed' || status === 'cancelled';
}

function startJobPolling() {
  stopJobPolling();
  jobPollingTimer = setInterval(() => {
    const view = document.getElementById('view-admin-product-research-config');
    if (view && view.classList.contains('active')) {
      const bVisible = !document.querySelector('[data-pr-track-panel="b"]')?.hidden;
      const legacyVisible = !document.querySelector('[data-pr-track-panel="legacy"]')?.hidden;
      const aVisible = !document.querySelector('[data-pr-track-panel="a"]')?.hidden;
      if (aVisible) void loadASchedule();
      if (bVisible) void mirrorController.loadSchedule();
      if (legacyVisible) void loadJobs();
    } else {
      stopJobPolling();
    }
  }, 8000);
}

function stopJobPolling() {
  if (jobPollingTimer) {
    clearInterval(jobPollingTimer);
    jobPollingTimer = null;
  }
  watchedJobTimers.forEach((timer) => clearInterval(timer));
  watchedJobTimers.clear();
}
